import React, { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Rocket, Camera, User, Sparkles } from 'lucide-react';
import { maskPhone } from '@/lib/validation';
import { CARGO_OPTIONS, OBJETIVO_OPTIONS, TAMANHO_OPTIONS, CONHECEU_OPTIONS } from '@/lib/constants';
import { trackUserIdentity, trackEvent } from '@/lib/gtm';
import { StandardModal } from '@/components/ui/StandardModal';
import { getProfileCompleteness } from '@/lib/profileCompleteness';
import { submitHubspotProfileForm, splitFullName } from '@/services/hubspotService';
import type { Database } from '@/types/supabase';

interface CompleteProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const CompleteProfileModal: React.FC<CompleteProfileModalProps> = ({ isOpen, onClose }) => {
    const { profile, user, refreshProfile } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // State dos campos
    const [fullName, setFullName] = useState('');
    const [telefone, setTelefone] = useState('');
    const [cargo, setCargo] = useState('');
    const [tamanho_empresa, setTamanhoEmpresa] = useState('');
    const [objetivo_principal, setObjetivoPrincipal] = useState('');
    const [como_conheceu, setComoConheceu] = useState('');

    // State do avatar
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { percent, missingCount } = getProfileCompleteness(profile);

    useEffect(() => {
        if (profile && isOpen) {
            setFullName(profile.full_name || '');
            setTelefone(profile.telefone || '');
            setCargo(profile.cargo || '');
            setTamanhoEmpresa(profile.tamanho_empresa || '');
            setObjetivoPrincipal(profile.objetivo_principal || '');
            setComoConheceu(profile.como_conheceu || '');
            setAvatarFile(null);
            setAvatarPreview(profile.avatar_url || null);
            setError('');
        }
    }, [profile, isOpen]);

    // Identifica quais campos ainda precisam ser preenchidos
    const needsAvatar = !profile?.avatar_url;
    const needsName = !profile?.full_name?.trim();
    const needsTelefone = !profile?.telefone?.trim();
    const needsCargo = !profile?.cargo?.trim();
    const needsTamanho = !profile?.tamanho_empresa?.trim();
    const needsObjetivo = !profile?.objetivo_principal?.trim();
    const needsComoConheceu = !profile?.como_conheceu?.trim();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.size > 2 * 1024 * 1024) {
                setError('A imagem selecionada deve ter no máximo 2MB.');
                return;
            }
            setAvatarFile(file);
            setAvatarPreview(URL.createObjectURL(file));
            setError('');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !profile) return;

        if (needsName && !fullName.trim()) {
            setError('Por favor, insira seu nome completo.');
            return;
        }

        if (needsTelefone && telefone && telefone.length < 14) {
            setError('Por favor, insira um telefone válido com DDD.');
            return;
        }

        if (
            (needsCargo && !cargo) || 
            (needsTamanho && !tamanho_empresa) || 
            (needsObjetivo && !objetivo_principal) || 
            (needsComoConheceu && !como_conheceu)
        ) {
            setError('Por favor, preencha todos os campos obrigatórios.');
            return;
        }

        setError('');
        setLoading(true);

        try {
            let uploadedAvatarUrl = profile.avatar_url;

            // Faz upload de avatar se um novo arquivo foi selecionado
            if (avatarFile) {
                const fileExt = avatarFile.name.split('.').pop();
                const fileName = `${crypto.randomUUID()}.${fileExt}`;
                const filePath = `${user.id}/${fileName}`;

                const { error: uploadError } = await supabase.storage
                    .from('user_avatars')
                    .upload(filePath, avatarFile, { upsert: true });

                if (uploadError) throw uploadError;

                const { data: { publicUrl } } = supabase.storage
                    .from('user_avatars')
                    .getPublicUrl(filePath);

                uploadedAvatarUrl = publicUrl;
            }

            const updates: Database['public']['Tables']['profiles']['Update'] = {
                avatar_url: uploadedAvatarUrl,
                full_name: fullName.trim() || profile.full_name,
                telefone: telefone.trim() || profile.telefone,
                cargo: cargo || profile.cargo,
                tamanho_empresa: tamanho_empresa || profile.tamanho_empresa,
                objetivo_principal: objetivo_principal || profile.objetivo_principal,
                como_conheceu: como_conheceu || profile.como_conheceu,
            };

            const { error: updateError } = await supabase
                .from('profiles')
                .update(updates)
                .eq('id', user.id);

            if (updateError) throw updateError;

            await refreshProfile();

            trackUserIdentity(
                { id: user.id, email: user.email },
                {
                    full_name: updates.full_name || null,
                    cargo: updates.cargo || null,
                    tamanho_empresa: updates.tamanho_empresa || null,
                    objetivo_principal: updates.objetivo_principal || null,
                    como_conheceu: updates.como_conheceu || null,
                }
            );
            trackEvent('onboarding_completed');

            // Verifica se o perfil atingiu 100% de completude
            const { isComplete: isAllComplete } = getProfileCompleteness(updates);

            // Sincroniza dados com o CRM HubSpot (Opção B - Formulário dedicado)
            if (user.email) {
                const { firstname, lastname } = splitFullName(updates.full_name || profile.full_name);
                submitHubspotProfileForm({
                    email: user.email,
                    firstname,
                    lastname,
                    phone: updates.telefone || null,
                    cargo: updates.cargo || null,
                    tamanho_empresa: updates.tamanho_empresa || null,
                    objetivo_principal: updates.objetivo_principal || null,
                    como_conheceu: updates.como_conheceu || null,
                    perfil_completo: isAllComplete ? 'Sim' : 'Não',
                    pageUri: typeof window !== 'undefined' ? window.location.href : undefined,
                    pageName: typeof document !== 'undefined' ? document.title : 'Completar Perfil',
                }).catch(err => console.warn('[HubSpot] Erro assíncrono ao sincronizar perfil:', err));
            }
            const alreadyCelebrated = localStorage.getItem('personalab_profile_celebrated') === 'true';

            if (isAllComplete && !alreadyCelebrated) {
                confetti({
                    particleCount: 150,
                    spread: 80,
                    origin: { y: 0.6 }
                });
                localStorage.setItem('personalab_profile_celebrated', 'true');
            }

            onClose();
        } catch (err) {
            console.error('Error saving profile:', err);
            setError('Não foi possível salvar suas informações. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const footerNode = (
        <div className="w-full flex flex-col-reverse sm:flex-row items-center justify-end gap-3">
            <Button 
                type="button" 
                variant="ghost" 
                onClick={onClose}
                className="w-full sm:w-auto text-slate-500"
            >
                Fechar
            </Button>
            <Button 
                type="submit" 
                form="complete-profile-form"
                loading={loading}
                className="w-full sm:w-auto px-8 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 shadow-lg shadow-indigo-200"
            >
                Salvar Informações
            </Button>
        </div>
    );

    const subtitleText = missingCount <= 1
        ? 'Falta apenas 1 item para completar seu perfil e desbloquear 100% da experiência!'
        : `Faltam ${missingCount} itens para completar seu perfil e desbloquear 100% da experiência!`;

    return (
        <StandardModal
            isOpen={isOpen}
            onClose={onClose}
            title="Complete seu Perfil"
            subtitle={subtitleText}
            icon={Rocket}
            iconColor="text-indigo-600"
            iconBgColor="bg-indigo-100"
            maxWidth="xl"
            footer={footerNode}
        >
            <form id="complete-profile-form" name="Completar Perfil PersonaLab" onSubmit={handleSubmit} className="space-y-4">
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-xl text-sm font-medium animate-in fade-in">
                        {error}
                    </div>
                )}

                {/* Barra de progresso sutil */}
                <div className="p-3 bg-indigo-50/60 border border-indigo-100/80 rounded-xl space-y-1.5">
                    <div className="flex items-center justify-between text-xs font-bold text-indigo-900">
                        <span className="flex items-center gap-1.5">
                            <Sparkles size={14} className="text-indigo-600" />
                            Progresso Atual
                        </span>
                        <span>{percent}% completo</span>
                    </div>
                    <div className="w-full bg-indigo-100 rounded-full h-1.5 overflow-hidden">
                        <div 
                            className="bg-indigo-600 h-1.5 rounded-full transition-all duration-500"
                            style={{ width: `${percent}%` }}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 pt-1">

                    {/* Foto de Perfil (se faltar) */}
                    {needsAvatar && (
                        <div className="flex flex-col items-center justify-center p-3.5 bg-slate-50 border border-dashed border-slate-200 rounded-2xl md:col-span-2 text-center gap-2">
                            <div 
                                className="relative group cursor-pointer" 
                                onClick={() => fileInputRef.current?.click()}
                                title="Clique para escolher uma foto"
                            >
                                <div className="w-16 h-16 rounded-full bg-white border-2 border-indigo-200 shadow-xs flex items-center justify-center overflow-hidden">
                                    {avatarPreview ? (
                                        <img src={avatarPreview} alt="Preview" className="w-full h-full object-cover" />
                                    ) : (
                                        <User size={28} className="text-slate-400" />
                                    )}
                                </div>
                                <div className="absolute -bottom-0.5 -right-0.5 p-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-md transition-transform group-hover:scale-110">
                                    <Camera size={12} />
                                </div>
                            </div>
                            <div className="space-y-0.5">
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="text-xs font-bold text-indigo-600 hover:text-indigo-700 underline underline-offset-2"
                                >
                                    {avatarPreview && avatarFile ? 'Alterar foto selecionada' : 'Adicionar foto de perfil'}
                                </button>
                                <p className="text-[11px] text-slate-500">JPG ou PNG de até 2MB</p>
                            </div>
                            <input 
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                        </div>
                    )}

                    {/* Nome Completo (se faltar) */}
                    {needsName && (
                        <div className="flex flex-col gap-1.5 md:col-span-2">
                            <label className="text-sm font-bold text-slate-700">Nome Completo <span className="text-red-500">*</span></label>
                            <input 
                                type="text"
                                className="w-full h-[46px] px-4 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all placeholder:text-slate-400"
                                placeholder="Ex: João Silva"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                            />
                        </div>
                    )}

                    {/* Telefone / WhatsApp (se faltar) */}
                    {needsTelefone && (
                        <div className="flex flex-col gap-1.5 md:col-span-2">
                            <label className="text-sm font-bold text-slate-700">Telefone / WhatsApp</label>
                            <input 
                                type="text"
                                className="w-full h-[46px] px-4 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all placeholder:text-slate-400"
                                placeholder="Ex: (11) 99999-9999"
                                value={telefone}
                                onChange={(e) => setTelefone(maskPhone(e.target.value))}
                                maxLength={15}
                            />
                        </div>
                    )}

                    {/* Cargo / Profissão (se faltar) */}
                    {needsCargo && (
                        <div className="flex flex-col gap-1.5 md:col-span-2">
                            <label className="text-sm font-bold text-slate-700">Cargo / Profissão <span className="text-red-500">*</span></label>
                            <Select 
                                className="w-full h-[46px] bg-white border-slate-200"
                                placeholder="Selecione seu cargo"
                                value={cargo}
                                onChange={setCargo}
                                options={CARGO_OPTIONS.map(opt => ({value: opt, label: opt}))}
                            />
                        </div>
                    )}

                    {/* Tamanho da Empresa (se faltar) */}
                    {needsTamanho && (
                        <div className="flex flex-col gap-1.5 md:col-span-2">
                            <label className="text-sm font-bold text-slate-700">Tamanho da Empresa <span className="text-red-500">*</span></label>
                            <Select 
                                className="w-full h-[46px] bg-white border-slate-200"
                                placeholder="Quantas pessoas?"
                                value={tamanho_empresa}
                                onChange={setTamanhoEmpresa}
                                options={TAMANHO_OPTIONS.map(opt => ({value: opt, label: opt}))}
                            />
                        </div>
                    )}

                    {/* Objetivo Principal (se faltar) */}
                    {needsObjetivo && (
                        <div className="flex flex-col gap-1.5 md:col-span-2">
                            <label className="text-sm font-bold text-slate-700">Objetivo Principal <span className="text-red-500">*</span></label>
                            <Select 
                                className="w-full h-[46px] bg-white border-slate-200"
                                placeholder="O que você busca?"
                                value={objetivo_principal}
                                onChange={setObjetivoPrincipal}
                                options={OBJETIVO_OPTIONS.map(opt => ({value: opt, label: opt}))}
                            />
                        </div>
                    )}

                    {/* Como nos conheceu (se faltar) */}
                    {needsComoConheceu && (
                        <div className="flex flex-col gap-1.5 md:col-span-2">
                            <label className="text-sm font-bold text-slate-700">Como nos conheceu? <span className="text-red-500">*</span></label>
                            <Select 
                                className="w-full h-[46px] bg-white border-slate-200"
                                placeholder="Onde nos viu?"
                                value={como_conheceu}
                                onChange={setComoConheceu}
                                options={CONHECEU_OPTIONS.map(opt => ({value: opt, label: opt}))}
                            />
                        </div>
                    )}
                </div>
            </form>
        </StandardModal>
    );
};
